import React from "react";
import { Constants } from "@eagerdog/constants";
import moment from "moment-timezone";

import { IDog, IShowElement, IEvent } from "@eagerdog/interfaces";
import { IShowDogs, IDogInfo } from "../EventRegister";

import DogList from "src/components/DogList";
import DogPortrait from "src/components/DogPortrait/DogPortrait";
import ShowElementsRow from "src/components/ShowElementsRow/ShowElementsRow";

import "./ShowTime.scss";

interface IProps {
  timezone: string,
  club: string,
  show: IShowDogs,
  className?: string,
  onToggleShow(): void,
  onAddDog(): void,
  onEditDog?(): void,
  onDogClick(dog: IDog): void,
  dogs: IDog[],
  onBehalf?: boolean,
  isClaimed?: boolean,
  getWaitlistAvailabilityText?: (show: IShowDogs) => string | JSX.Element | null,
  isShowFull?: (show: IShowDogs) => boolean,
  event?: IEvent,
  isVolunteer?: boolean,
  volunteerLateRegAllowed?: boolean,
  volunteerEarlyRegAllowed?: boolean,
  isClubManager?: boolean
}

const ShowTime: React.FC<IProps> = (props) => {
  const showDogs = () => {
    return props.show.dogs.map((dog) => dog)
  }

  const registrationClosed = () => {
    // Club managers can always register (whether on behalf or for themselves)
    if (props.isClubManager || props.onBehalf === true) {
      return false;
    }
    
    // If user is a volunteer and setting is enabled, never closed
    if (props.isVolunteer && props.volunteerLateRegAllowed) {
      return false;
    }
    
    // Otherwise, check the registration end date
    return props.show.use_registration_end_date && moment(props.show.registration_end_date).format("X") <= moment(new Date()).format("X");
  }

  const isDisabled = () => {
    // Club managers and on-behalf users can always register, even when full or closed
    if (props.isClubManager || props.onBehalf === true) {
      return false;
    }
    
    // Check both registration closed and show full status
    return registrationClosed() || (props.isShowFull && props.isShowFull(props.show));
  }

  const getRemainingSpots = () => {
    if (props.show.max_dogs) {
      return ((props.show.max_dogs || 0) - (props.show.registered_entries || 0));
    }

    return 0;
  }

  const hasAvailableSpot = () => {
    return getRemainingSpots() > 0;
  }

  const getPrecisionCourseName = () => {
    if (props.show.show_type === Constants.show_type.precision_coursing) {
      let hasDash = props.show.show_elements.filter((s: any) => { return s.level === Constants.dog_class_element_level.dash });
      let hasSteeplechase = props.show.show_elements.filter((s: any) => { return s.level === Constants.dog_class_element_level.steeplechase });

      if (hasDash.length !== 0 && hasSteeplechase.length === 0) {
        return " ("+ Constants.dog_class_element_level.dash +")";
      }

      if (hasDash.length === 0 && hasSteeplechase.length !== 0) {
        return " ("+ Constants.dog_class_element_level.steeplechase +")";
      }
    }

    return "";
  }

  const getAvailabilityText = () => {
    const waitlistText = props.getWaitlistAvailabilityText?.(props.show);

    // Show waitlist/capacity availability if it exists and registration is open for the user
    if (waitlistText && !isDisabled()) {
      return waitlistText;
    }

    // If waitlist is based on daily judge/trial time limit, don't show any limit info
    if (props.show.waitlist_option && [Constants.waitlist_options.daily_judge_time_limit, Constants.waitlist_options.daily_trial_time_limit].includes(props.show.waitlist_option)) {
      return '';
    }

    // Show remaining spots if max_dogs is set
    if (props.show.max_dogs !== undefined && !isDisabled()) {
      return getRemainingSpots() > 0 ? `${getRemainingSpots()} Spots Remaining` : 'Full';
    }

    return '';
  }

  return (
    <div className={isDisabled() ? "ShowTime closed " + props?.className : "ShowTime " + props?.className}>
      <div className="showTitle">
        <div className="showText">
          {props.event?.sanctioning_club === Constants.sanctioning_club.AKC && props.show.event_number && props.show.custom_show_name && (
            <span className="customShowName" onClick={(e) => { if (!isDisabled()) { props.onToggleShow(); }}}>{props.show.custom_show_name}</span>
          )}
          <span className="time" onClick={(e) => { if (!isDisabled()) { props.onToggleShow(); }}}>
            {props.show.trailing_show
              ? "Follows " + props.show.previous_show_name
              : <>
                  <span className="dateDesktop">{moment(props.show.show_date).tz(props.timezone).format("dddd MMMM Do, YYYY @ h:mmA zz")}</span>
                  <span className="dateMobile">{moment(props.show.show_date).tz(props.timezone).format("ddd. MMMM Do, YYYY @ h:mmA zz")}</span>
                </>
            }
          </span>
          <div className="showMetaLine" onClick={(e) => { if (!isDisabled()) { props.onToggleShow(); }}}>
            <span className="titleText">{`${props.show.show_type}${props.event?.sanctioning_club === Constants.sanctioning_club.AKC && props.show.event_number 
              ? ` (Event #: ${props.show.event_number})`
              : ` - ${props.show.custom_show_name || props.show.show_name}`
            }${getPrecisionCourseName()}`}</span>
            {getAvailabilityText() && <span className="waitlistAvailability inline">{getAvailabilityText()}</span>}
            {(() => {
            const canBypassClosedDate = props.isClubManager || props.onBehalf;
            // Show "Registration Closed" message if registration date has passed
            const actuallyRegistrationClosed = props.show.use_registration_end_date && moment(props.show.registration_end_date).format("X") <= moment(new Date()).format("X");
            if (actuallyRegistrationClosed && !canBypassClosedDate) {
              return <span>Registration Closed</span>;
            }
            // Show "Trial at capacity" message if the show is full (e.g. MRV limit reached)
            const showIsFull = props.isShowFull && props.isShowFull(props.show);
            if (showIsFull && !canBypassClosedDate) {
              return <span className="capacityClosedMessage">Trial at capacity - Contact the show secretary to inquire about availability</span>;
            }
            return null;
          })()}</div>
          {!(props.isShowFull && props.isShowFull(props.show) && !props.isClubManager && !props.onBehalf) && (
            <ul className="selectedDogsMobile">
              {props.show.dogs.map((dog:IDogInfo, dogIndex:number) => {
                return(
                  <li key={dogIndex}>
                    <div className="dogPortrait"><DogPortrait edit={true} show={props.show} onEditDog={props.onEditDog ? props.onEditDog : undefined} showName={true} dog={dog} sanctioningClub={props.event?.sanctioning_club} isAdmin={true} isDisabled={isDisabled()} /></div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="showActions">
          {!(props.isShowFull && props.isShowFull(props.show) && !props.isClubManager && !props.onBehalf) && (
            <ul className="selectedDogs">
              {props.show.dogs.map((dog:IDogInfo, dogIndex:number) => {
                return(
                  <li key={dogIndex}>
                    <div className="dogPortrait"><DogPortrait edit={true} show={props.show} onEditDog={props.onEditDog ? props.onEditDog : undefined} showName={true} dog={dog} sanctioningClub={props.event?.sanctioning_club} isAdmin={true} isDisabled={isDisabled()} /></div>
                  </li>
                );
              })}
            </ul>
          )}
          {!isDisabled() && <div onClick={(e) => { if (!isDisabled()) { props.onToggleShow(); }}} className="icon caretDown"></div>}
        </div>
      </div>
      {!isDisabled() && (
        <div className="collapsibleContent">
          <ShowElementsRow show={props.show} onClick={() => { props.onToggleShow(); }} />
          {!(props.isShowFull && props.isShowFull(props.show) && !props.isClubManager && !props.onBehalf) && (
            <DogList
              club={props.club}
              show={props.show}
              dogs={props.dogs}
              selectedDogs={showDogs()}
              hasAvailableSpot={hasAvailableSpot()}
              onDogClick={props.onDogClick}
              onAddDog={props.onAddDog}
              onEditDog={props.onEditDog}
              event={props.event}
              isAdmin={props.isClubManager}
              isDisabled={false}
              showSelectionHint={true}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ShowTime;
